import React from 'react';
import { Create, useForm, useSelect } from '@refinedev/antd';
import { Form, Input, Select, InputNumber, Switch, Button, Card, theme } from 'antd';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { useSearchParams } from 'react-router-dom';
import { QuestionType } from '@wellverse/shared-ts';

export const SurveyQuestionCreate: React.FC = () => {
  const { token } = theme.useToken();
  const { formProps, saveButtonProps } = useForm({ resource: 'survey-questions' });
  const { selectProps } = useSelect({
    resource: 'survey-screens',
    optionLabel: 'screenKey',
    optionValue: 'id',
  });
  const [searchParams] = useSearchParams();
  const screenId = searchParams.get('screenId');

  return (
    <Create resource="survey-questions" saveButtonProps={saveButtonProps}>
      <Form {...formProps} layout="vertical" initialValues={{ screenId: screenId || undefined }}>
        <Form.Item label="Screen" name="screenId" rules={[{ required: true }]}>
          <Select {...selectProps} placeholder="Select a screen" />
        </Form.Item>
        <Form.Item label="Question Key" name="questionKey" rules={[{ required: true }]}>
          <Input placeholder="e.g. primary_goal" />
        </Form.Item>
        <Form.Item label="Question Text" name="questionText" rules={[{ required: true }]}>
          <Input placeholder="What is your primary goal?" />
        </Form.Item>
        <Form.Item label="Type" name="type" rules={[{ required: true }]} initialValue={QuestionType.SINGLE_CHOICE}>
          <Select>
            {Object.values(QuestionType).map((t) => (
              <Select.Option key={t} value={t}>
                {t}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item label="Order Index" name="orderIndex" rules={[{ required: true }]} initialValue={0}>
          <InputNumber style={{ width: '100%' }} min={0} />
        </Form.Item>
        <Form.Item label="Required" name="isRequired" valuePropName="checked" initialValue={true}>
          <Switch />
        </Form.Item>
        <Form.Item label="Active" name="isActive" valuePropName="checked" initialValue={true}>
          <Switch />
        </Form.Item>
        <Form.Item
          label="Validation (JSON)"
          name="validation"
          getValueProps={(value) => ({ value: value ? JSON.stringify(value, null, 2) : '' })}
          normalize={(value) => {
            if (!value || typeof value !== 'string') return value;
            const trimmed = value.trim();
            if (!trimmed) return null;
            try { return JSON.parse(trimmed); } catch { return '__INVALID_JSON__'; }
          }}
          rules={[
            {
              validator: (_, value) => {
                if (value === '__INVALID_JSON__') {
                  return Promise.reject(new Error('Invalid JSON format'));
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <Input.TextArea rows={3} placeholder='{"minLength":1,"maxLength":100}' />
        </Form.Item>

        <Form.List name="options">
          {(fields, { add, remove }) => (
            <div>
              {fields.map((field, index) => (
                <Card
                  key={field.key}
                  size="small"
                  title={`Option ${index + 1}`}
                  extra={
                    <MinusCircleOutlined onClick={() => remove(field.name)} />
                  }
                  style={{ marginBottom: 16, background: token.colorBgContainer, borderColor: token.colorBorder }}
                >
                  <Form.Item
                    label="Value"
                    name={[field.name, 'value']}
                    rules={[{ required: true }]}
                  >
                    <Input placeholder="lose_weight" />
                  </Form.Item>
                  <Form.Item
                    label="Label"
                    name={[field.name, 'label']}
                    rules={[{ required: true }]}
                  >
                    <Input placeholder="Lose Weight" />
                  </Form.Item>
                  <Form.Item
                    label="Order Index"
                    name={[field.name, 'orderIndex']}
                    rules={[{ required: true }]}
                    initialValue={index}
                  >
                    <InputNumber style={{ width: '100%' }} min={0} />
                  </Form.Item>
                  <Form.Item
                    label="Icon"
                    name={[field.name, 'icon']}
                  >
                    <Input placeholder="Optional icon name/URL" />
                  </Form.Item>
                </Card>
              ))}
              <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                Add Option
              </Button>
            </div>
          )}
        </Form.List>
      </Form>
    </Create>
  );
};
